import { useSyncExternalStore } from 'react'
import { captureWriteIdentity, type WriteIdentity } from '@/core/api/http'
import type { CardMark } from '@/core/views/viewConfig'
import type { VerbisLebenslaufUploadResult } from '../../api/verbisLebenslauf'
import type { VerbisLebenslaufUploadFailure } from './errors'
import type { VerbisLebenslaufEintrag } from './VerbisLebenslaufHochladen'

export type LaufenderUpload =
  | { phase: 'laeuft'; eintraege: readonly VerbisLebenslaufEintrag[] }
  | { phase: 'fehler'; eintraege: readonly VerbisLebenslaufEintrag[]; failure: VerbisLebenslaufUploadFailure }
  | { phase: 'fertig'; result: VerbisLebenslaufUploadResult }

export const LAEUFT_TEXT = "VerBIS und Lebenslauf werden ausgelesen …"

export function failureMeldung(failure: VerbisLebenslaufUploadFailure): string {
  return failure.kind === 'prompt' ? failure.body.meldung : failure.message
}

type UploadWorkspace = {
  uploads: ReadonlyMap<string, LaufenderUpload>
  marks: ReadonlyMap<string, CardMark>
  offen: Map<string, number>
}
const workspaces = new Map<string, UploadWorkspace>()
const listeners = new Set<() => void>()

function workspace(identity: WriteIdentity = captureWriteIdentity()): UploadWorkspace {
  const key = JSON.stringify(identity)
  let value = workspaces.get(key)
  if (!value) {
    value = { uploads: new Map(), marks: new Map(), offen: new Map() }
    workspaces.set(key, value)
  }
  return value
}

function markFor(upload: LaufenderUpload): CardMark | null {
  if (upload.phase === 'laeuft') return { tone: 'warning', text: LAEUFT_TEXT, busy: true }
  if (upload.phase === 'fehler') return { tone: 'danger', text: `Fehlgeschlagen: ${failureMeldung(upload.failure)}` }
  return null
}

export function setUpload(coachingId: string, upload: LaufenderUpload, identity: WriteIdentity): void {
  const owner = workspace(identity)
  owner.uploads = new Map(owner.uploads).set(coachingId, upload)
  const nextMarks = new Map<string, CardMark>()
  for (const [id, entry] of owner.uploads) {
    const mark = markFor(entry)
    if (mark) nextMarks.set(id, mark)
  }
  owner.marks = nextMarks
  for (const listener of listeners) listener()
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function useLaufenderUpload(coachingId: string | null): LaufenderUpload | undefined {
  const snapshot = () => (coachingId ? workspace().uploads.get(coachingId) : undefined)
  return useSyncExternalStore(subscribe, snapshot, snapshot)
}

export function boxOffen(coachingId: string): () => void {
  const { offen } = workspace()
  offen.set(coachingId, (offen.get(coachingId) ?? 0) + 1)
  return () => {
    const n = (offen.get(coachingId) ?? 1) - 1
    if (n > 0) offen.set(coachingId, n)
    else offen.delete(coachingId)
  }
}

export function istBoxOffen(coachingId: string): boolean {
  return workspace().offen.has(coachingId)
}

export const laufendeUploads = {
  subscribe,
  cardMarks: (): ReadonlyMap<string, CardMark> => workspace().marks,
}
